import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getPublicScreen, startPublicScreen, completePublicScreen, markQuestionSeen, heartbeatPublicScreen } from "../api";
import { savePending, deletePending, getOldestPending, clearToken, cleanupOldPending } from "../screeningStore";
import { AsyncButton } from "../components/AsyncButton";

type Question = { id: number; order_index: number; question_text: string; time_limit_seconds: number };
type Phase = "loading" | "consent" | "resume" | "techcheck" | "practice" | "question" | "uploading" | "done" | "error" | "expired" | "abandoned" | "already_done";

const btnStyle: React.CSSProperties = { background: "#0f766e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 28px", cursor: "pointer", fontWeight: 700, fontSize: 16 };
const outlineBtn: React.CSSProperties = { background: "#fff", color: "#0f766e", border: "2px solid #0f766e", borderRadius: 8, padding: "11px 24px", cursor: "pointer", fontWeight: 600, fontSize: 15 };

const DEFAULT_PRACTICE_Q = "Tell us about yourself and why you're interested in this role.";

const PUBLIC_API = (process.env.REACT_APP_API_URL || "http://localhost:30801");

// Pick the best-supported recording container.
function pickRecorderMime(): string {
  return MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9"
    : MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";
}

// Cap the encoder bitrate so a talking-head answer stays small on slow connections.
// ~564 kbps total ≈ 8 MB for a 2-minute answer, vs 100+ MB at browser defaults — a 10-25x
// reduction with negligible quality loss for an interview. This is the single biggest lever
// for candidates on weak/mobile networks.
function recorderOptions(mimeType: string): MediaRecorderOptions {
  return { mimeType, videoBitsPerSecond: 500_000, audioBitsPerSecond: 64_000 };
}

function BrandHeader({ companyName, jobTitle, logoUrl, isPractice }: {
  companyName?: string; jobTitle?: string; logoUrl?: string | null; isPractice?: boolean;
}) {
  return (
    <div style={{ background: "#0f172a", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {logoUrl ? (
          <img src={logoUrl} alt="" style={{ height: 28, width: "auto", borderRadius: 4, objectFit: "contain", flexShrink: 0 }} onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
        ) : (
          <div style={{ width: 28, height: 28, borderRadius: 6, background: "#0f766e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
            {(companyName || "C")[0].toUpperCase()}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{companyName}</div>
          {jobTitle && <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{isPractice ? "Warm-up · " : ""}{jobTitle}</div>}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
        <img src="/logo-dark.png" alt="HRxAI" style={{ height: 18, width: "auto", objectFit: "contain", opacity: 0.85 }}
          onError={e => { const t = e.target as HTMLImageElement; t.style.display = "none"; }} />
      </div>
    </div>
  );
}

function PoweredByFooter({ current, total }: { current?: number; total?: number }) {
  return (
    <div style={{ background: "#0f172a", borderTop: "1px solid rgba(255,255,255,0.06)", padding: "8px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      {current != null && total != null ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#64748b" }}>Question {current} of {total}</span>
          <div style={{ width: 80, height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2 }}>
            <div style={{ width: `${(current / total) * 100}%`, height: "100%", background: "#0f766e", borderRadius: 2, transition: "width 0.3s" }} />
          </div>
        </div>
      ) : <span />}
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ fontSize: 10, color: "#475569" }}>Powered by</span>
        <div style={{ width: 14, height: 14, borderRadius: 3, background: "#0f766e", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 7, fontWeight: 900, color: "#fff" }}>HR</span>
        </div>
        <span style={{ fontSize: 10, color: "#0f766e", fontWeight: 700 }}>HRxAI</span>
      </div>
    </div>
  );
}

export default function ScreenPublicPage() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>("loading");
  const [sessionData, setSessionData] = useState<any>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [recordState, setRecordState] = useState<"ready" | "countdown" | "recording">("ready");
  const [countdown, setCountdown] = useState(3);
  const [timeLeft, setTimeLeft] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMsg, setUploadMsg] = useState("");
  const [pendingUpload, setPendingUpload] = useState<{ blob: Blob; q: Question; qIndex: number; total: number; switches: number; duration: number } | null>(null);
  const [slowConnection, setSlowConnection] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  // Connection-speed probe (#5): measured from the tech-check clip upload
  const [connectionKbps, setConnectionKbps] = useState<number | null>(null);
  const [connectionWarn, setConnectionWarn] = useState(false);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [answeredQuestionIds, setAnsweredQuestionIds] = useState<number[]>([]);
  const [seenQuestionIds, setSeenQuestionIds] = useState<number[]>([]);
  const [fullscreenExits, setFullscreenExits] = useState(0);
  // Per-question re-record attempt counter. Incremented each time a "too short" rejection
  // causes the candidate to discard their answer and record again.
  const reRecordAttemptsRef = useRef<Record<number, number>>({});
  const [micLevel, setMicLevel] = useState(0); // 0–100 for the live level bar
  const [faceWarning, setFaceWarning] = useState(false);
  const [sessionSecondsLeft, setSessionSecondsLeft] = useState<number | null>(null);
  const [recordError, setRecordError] = useState<string>(""); // inline error on question screen
  // Tech check state
  const [techState, setTechState] = useState<"preview" | "recording" | "playback" | "ok">("preview");
  const [techBlob, setTechBlob] = useState<Blob | null>(null);
  const techPlaybackRef = useRef<HTMLVideoElement>(null);
  // Practice state
  const [practiceState, setPracticeState] = useState<"ready" | "countdown" | "recording" | "done">("ready");
  const [practiceCountdown, setPracticeCountdown] = useState(3);
  const [practiceTimeLeft, setPracticeTimeLeft] = useState(60);
  const [practiceRecordingDur, setPracticeRecordingDur] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const durationRef = useRef(0);
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const tabSwitchesRef = useRef(0);
  const isRecordingRef = useRef(false);
  const fullscreenExitsRef = useRef(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micAnimFrameRef = useRef<number | null>(null);
  const faceCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const noFaceSecondsRef = useRef(0);
  const noFaceEventsRef = useRef(0);
  const noFaceActiveRef = useRef(false);
  const sessionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!token) { setPhase("error"); setErrorMsg("Invalid link."); return; }
    cleanupOldPending(); // remove stale IndexedDB entries > 24h
    getPublicScreen(token)
      .then(async data => {
        setSessionData(data);
        if (data.status === "expired") { clearToken(token); setPhase("expired"); return; }
        if (["completed", "processing", "scored", "error"].includes(data.status)) {
          clearToken(token); setPhase("already_done"); return;
        }

        // Refresh-recovery (#2): a blob recorded but never confirmed-uploaded survives in
        // IndexedDB across a reload. Recover it and drop the candidate straight onto the
        // retry screen so they re-upload the saved video instead of re-recording.
        const pending = await getOldestPending(token);
        if (pending) {
          const answeredIds: number[] = data.answered_question_ids || [];
          const q = (data.questions || []).find((x: Question) => x.id === pending.questionId);
          if (q && !answeredIds.includes(pending.questionId)) {
            setAnsweredQuestionIds(answeredIds);
            setQuestionIndex(pending.qIndex);
            setPendingUpload({
              blob: pending.blob, q, qIndex: pending.qIndex, total: pending.total,
              switches: pending.switches, duration: pending.duration,
            });
            setPhase("question"); // renders the pendingUpload retry screen
            return;
          }
          // Stale (already answered server-side, or question removed) — discard it.
          await deletePending(token, pending.questionId);
        }

        if (data.status === "in_progress") {
          // Browser crash / closed tab — offer resume
          setAnsweredQuestionIds(data.answered_question_ids || []);
          setSeenQuestionIds(data.seen_question_ids || []);
          setPhase("resume");
          return;
        }
        setPhase("consent");
      })
      .catch(async (err) => {
        const status = err?.status || err?.response?.status;
        if (status === 410) {
          // Could be link-expired OR session-abandoned (20-min window)
          const body = await err.json?.().catch(() => ({}));
          if (body?.detail?.includes("abandoned") || body?.detail?.includes("Session expired")) {
            setPhase("abandoned");
          } else {
            setPhase("expired");
          }
          return;
        }
        setPhase("error"); setErrorMsg("Could not load your screening link. It may be invalid or expired.");
      });
  }, [token]);

  // Tab-switch / focus-loss detection: fire during recording OR the "ready" overlay
  // (when the question is visible). Uses both visibilitychange and window.blur so that
  // Alt+Tab to another app is caught even when the browser stays "visible" on macOS.
  useEffect(() => {
    let lastFiredAt = 0; // deduplicate if both events fire for the same action

    function recordSwitch() {
      if (!isRecordingRef.current) return;
      const now = Date.now();
      if (now - lastFiredAt < 500) return; // debounce — same action fires both events
      lastFiredAt = now;
      tabSwitchesRef.current++;
      setTabSwitches(tabSwitchesRef.current);
      setShowTabWarning(true);
    }

    function onVisibilityChange() {
      if (document.hidden) recordSwitch();
    }
    function onBlur() {
      recordSwitch();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  useEffect(() => () => {
    stopStream();
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  // Option B: mark question seen + auto-skip "Ready?" overlay on resume.
  // Fires whenever the ready overlay is shown. First visit → mark seen (fire-and-forget).
  // On resume → question already in seenQuestionIds → go straight to countdown.
  useEffect(() => {
    if (phase !== "question" || recordState !== "ready" || !currentQuestion || !token) return;
    if (seenQuestionIds.includes(currentQuestion.id)) {
      // Already seen in a previous session — skip overlay, start countdown immediately
      startCountdown();
    } else {
      // First time seeing this question — mark it on the backend
      markQuestionSeen(token, currentQuestion.id);
      setSeenQuestionIds(prev => prev.includes(currentQuestion.id) ? prev : [...prev, currentQuestion.id]);
    }
  }, [phase, questionIndex, recordState]); // eslint-disable-line

  // Track fullscreen exits during the interview so they are sent as an integrity signal.
  useEffect(() => {
    function onFsChange() {
      if (!document.fullscreenElement && isRecordingRef.current) {
        fullscreenExitsRef.current++;
        setFullscreenExits(fullscreenExitsRef.current);
      }
    }
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Heartbeat: keep the backend session alive while answering.
  useEffect(() => {
    if (phase !== "question" || !token) return;
    const interval = setInterval(() => heartbeatPublicScreen(token), 30_000);
    return () => clearInterval(interval);
  }, [phase, token]);

  // Session countdown: surface a warning when < 5 min remain in the 20-min window.
  useEffect(() => {
    if (!sessionData?.started_at) return;
    const startedAt = new Date(sessionData.started_at).getTime();
    const windowMs = 20 * 60 * 1000;
    function tick() {
      const elapsed = Date.now() - startedAt;
      setSessionSecondsLeft(Math.max(0, Math.ceil((windowMs - elapsed) / 1000)));
    }
    tick();
    sessionTimerRef.current = setInterval(tick, 1000);
    return () => { if (sessionTimerRef.current) clearInterval(sessionTimerRef.current); };
  }, [sessionData?.started_at]);

  function stopMicFaceTracking() {
    if (micAnimFrameRef.current != null) cancelAnimationFrame(micAnimFrameRef.current);
    micAnimFrameRef.current = null;
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    analyserRef.current = null;
    setMicLevel(0);
    if (faceCheckRef.current) clearInterval(faceCheckRef.current);
    faceCheckRef.current = null;
    setFaceWarning(false);
    noFaceSecondsRef.current = 0;
  }

  const stopStream = () => {
    stopMicFaceTracking();
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const currentQuestion: Question | undefined = sessionData?.questions?.[questionIndex];
  const totalQuestions = sessionData?.questions?.length || 0;
  const practiceQ: string = sessionData?.practice_question || DEFAULT_PRACTICE_Q;
  const logoUrl = sessionData?.has_logo
    ? `${PUBLIC_API}/api/branding/public-logo?tenant_id=${sessionData.tenant_id}`
    : null;

  async function requestCamera() {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    streamRef.current = stream;
    return stream;
  }

  // Attach stream to visible video element
  useEffect(() => {
    if ((phase === "techcheck" || phase === "question" || phase === "practice") && streamRef.current && liveVideoRef.current) {
      liveVideoRef.current.srcObject = streamRef.current;
    }
  }, [phase, questionIndex, recordState, techState, practiceState]);

  // ── Submit all when videos are already uploaded (recovery path) ──────────
  async function handleSubmitAll() {
    setPhase("uploading");
    setUploadProgress(90);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await completePublicScreen(token!);
        clearToken(token!);
        stopStream();
        setPhase("done");
        return;
      } catch (err: any) {
        const status = err?.response?.status || err?.status;
        if (status === 409) { clearToken(token!); stopStream(); setPhase("done"); return; }
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    setUploadProgress(0);
    setPhase("resume");
    alert("Submission failed. Please check your connection and try again.");
  }

  // ── Resume after crash ────────────────────────────────────────────────────
  async function handleResume() {
    try {
      await startPublicScreen(token!); // increments resume_count on backend
      await requestCamera();
      // Find first unanswered question
      const questions: Question[] = sessionData?.questions || [];
      const answered = answeredQuestionIds;
      const firstUnanswered = questions.findIndex((q: Question) => !answered.includes(q.id));
      const startIdx = firstUnanswered >= 0 ? firstUnanswered : 0;
      isRecordingRef.current = true;
      document.documentElement.requestFullscreen?.().catch(() => {});
      setQuestionIndex(startIdx);
      setRecordState("ready");
      if (liveVideoRef.current && streamRef.current) {
        liveVideoRef.current.srcObject = streamRef.current;
      }
      setPhase("question");
    } catch {
      setPhase("error"); setErrorMsg("Could not access camera/microphone. Please allow access and try again.");
    }
  }

  // ── Consent → Tech Check ──────────────────────────────────────────────────
  async function handleStart() {
    try {
      await startPublicScreen(token!);
      await requestCamera();
      setTechState("preview");
      setPhase("techcheck");
    } catch {
      setPhase("error"); setErrorMsg("Could not access camera/microphone. Please allow access and try again.");
    }
  }

  // ── Tech Check ────────────────────────────────────────────────────────────
  function startTechRecording() {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = pickRecorderMime();
    const recorder = new MediaRecorder(streamRef.current, recorderOptions(mimeType));
    recorderRef.current = recorder;
    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setTechBlob(blob);
      setTechState("playback");
      // Kick off a real upload-throughput probe using this test clip (#5).
      measureConnection(blob);
    };
    recorder.start(500);
    setTechState("recording");
    // Auto-stop after 5s
    setTimeout(() => {
      if (recorder.state === "recording") recorder.stop();
    }, 5000);
  }

  function playTechRecording() {
    if (techBlob && techPlaybackRef.current) {
      if (techPlaybackRef.current.src?.startsWith("blob:")) {
        URL.revokeObjectURL(techPlaybackRef.current.src);
      }
      techPlaybackRef.current.src = URL.createObjectURL(techBlob);
      techPlaybackRef.current.play();
    }
  }

  function confirmTechCheck() {
    if (techPlaybackRef.current?.src?.startsWith("blob:")) {
      URL.revokeObjectURL(techPlaybackRef.current.src);
      techPlaybackRef.current.src = "";
    }
    if (liveVideoRef.current && streamRef.current) {
      liveVideoRef.current.srcObject = streamRef.current;
    }
    setPracticeState("ready");
    setPracticeTimeLeft(60);
    setPracticeRecordingDur(0);
    setPhase("practice");
    // Run bandwidth check in background — warn if upload speed < ~150 KB/s
    (async () => {
      try {
        const probe = new Blob([new Uint8Array(150 * 1024)], { type: "application/octet-stream" });
        const t0 = Date.now();
        await fetch(`${PUBLIC_API}/api/public/screen/speedtest`, { method: "POST", body: probe, credentials: "include" });
        const kbps = 150 / ((Date.now() - t0) / 1000);
        if (kbps < 150) setSlowConnection(true);
      } catch { /* ignore — don't block candidate */ }
    })();
  }

  function retryTechCheck() {
    if (techPlaybackRef.current?.src?.startsWith("blob:")) {
      URL.revokeObjectURL(techPlaybackRef.current.src);
      techPlaybackRef.current.src = "";
    }
    setTechBlob(null);
    setTechState("preview");
    if (liveVideoRef.current && streamRef.current) {
      liveVideoRef.current.srcObject = streamRef.current;
    }
  }

  // ── Practice Question ─────────────────────────────────────────────────────
  function startPracticeCountdown() {
    setPracticeState("countdown");
    setPracticeCountdown(3);
    let c = 3;
    const t = setInterval(() => {
      c--;
      setPracticeCountdown(c);
      if (c <= 0) { clearInterval(t); startPracticeRecording(); }
    }, 1000);
  }

  function startPracticeRecording() {
    if (!streamRef.current) return;
    chunksRef.current = [];
    durationRef.current = 0;
    setPracticeTimeLeft(60);
    setPracticeRecordingDur(0);
    const mimeType = pickRecorderMime();
    const recorder = new MediaRecorder(streamRef.current, recorderOptions(mimeType));
    recorderRef.current = recorder;
    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => setPracticeState("done");
    recorder.start(1000);
    setPracticeState("recording");
    timerRef.current = setInterval(() => {
      durationRef.current++;
      setPracticeRecordingDur(durationRef.current);
      setPracticeTimeLeft(t => {
        if (t <= 1) { stopPracticeRecording(); return 0; }
        return t - 1;
      });
    }, 1000);
  }

  function stopPracticeRecording() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }

  function skipPractice() {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    goToFirstQuestion();
  }

  function goToFirstQuestion() {
    tabSwitchesRef.current = 0;
    setTabSwitches(0);
    setShowTabWarning(false);
    fullscreenExitsRef.current = 0;
    setFullscreenExits(0);
    isRecordingRef.current = true;
    document.documentElement.requestFullscreen?.().catch(() => {});
    setQuestionIndex(0);
    setRecordState("ready");
    if (liveVideoRef.current && streamRef.current) {
      liveVideoRef.current.srcObject = streamRef.current;
    }
    setPhase("question");
  }

  // ── Real Recording ─────────────────────────────────────────────────────────
  function startCountdown() {
    isRecordingRef.current = true; // start tracking focus from the moment candidate clicks record
    setRecordState("countdown");
    setCountdown(3);
    let c = 3;
    const t = setInterval(() => {
      c--;
      setCountdown(c);
      if (c <= 0) { clearInterval(t); startRecording(); }
    }, 1000);
  }

  function startRecording() {
    if (!streamRef.current || !currentQuestion) return;
    const q = currentQuestion;
    const qIndex = questionIndex;
    const total = totalQuestions;

    chunksRef.current = [];
    durationRef.current = 0;
    tabSwitchesRef.current = 0;
    setTabSwitches(0);
    setShowTabWarning(false);
    noFaceEventsRef.current = 0;
    noFaceActiveRef.current = false;
    setRecordError("");
    const limit = q.time_limit_seconds || sessionData?.time_limit_seconds || 120;
    setTimeLeft(limit);
    setRecordingDuration(0);
    isRecordingRef.current = true;

    const mimeType = pickRecorderMime();

    const recorder = new MediaRecorder(streamRef.current, recorderOptions(mimeType));
    recorderRef.current = recorder;
    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      isRecordingRef.current = false;
      const blob = new Blob(chunksRef.current, { type: mimeType });
      autoSubmit(blob, q, qIndex, total, tabSwitchesRef.current, durationRef.current);
    };
    recorder.start(1000);
    setRecordState("recording");

    // Mic level meter via AudioContext analyser
    try {
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(streamRef.current!);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const animateMic = () => {
        analyser.getByteFrequencyData(buf);
        const avg = buf.reduce((s, v) => s + v, 0) / buf.length;
        setMicLevel(Math.min(100, Math.round(avg * 100 / 128)));
        micAnimFrameRef.current = requestAnimationFrame(animateMic);
      };
      micAnimFrameRef.current = requestAnimationFrame(animateMic);
    } catch { /* AudioContext unavailable — degrade gracefully */ }

    // Face presence check every 5 s.
    // Uses FaceDetector Web API (Chrome 74+) when available; falls back to canvas
    // brightness to at least catch a covered/very-dark camera.
    noFaceSecondsRef.current = 0;
    const _hasFaceDetector = typeof (window as any).FaceDetector !== "undefined";
    const _faceDetector = _hasFaceDetector
      ? new (window as any).FaceDetector({ fastMode: true, maxDetectedFaces: 1 })
      : null;
    faceCheckRef.current = setInterval(async () => {
      const video = liveVideoRef.current;
      if (!video || video.readyState < 2) return;
      let noFace = false;
      if (_faceDetector) {
        try {
          const faces = await _faceDetector.detect(video);
          noFace = faces.length === 0;
        } catch { noFace = false; /* ignore detection errors */ }
      } else {
        // Fallback: brightness check — only flags camera-covered / very dark situations
        const canvas = document.createElement("canvas");
        canvas.width = 64; canvas.height = 48;
        const ctx2d = canvas.getContext("2d");
        if (ctx2d) {
          ctx2d.drawImage(video, 0, 0, 64, 48);
          const pxData = ctx2d.getImageData(0, 0, 64, 48).data;
          let sum = 0;
          for (let i = 0; i < pxData.length; i += 4) sum += (pxData[i] + pxData[i + 1] + pxData[i + 2]) / 3;
          noFace = sum / (pxData.length / 4) < 15;
        }
      }
      if (noFace) {
        noFaceSecondsRef.current += 5;
        if (noFaceSecondsRef.current >= 10) {
          setFaceWarning(true);
          if (!noFaceActiveRef.current) {
            noFaceActiveRef.current = true;
            noFaceEventsRef.current++;
          }
        }
      } else {
        noFaceSecondsRef.current = 0;
        noFaceActiveRef.current = false;
        setFaceWarning(false);
      }
    }, 5000);

    timerRef.current = setInterval(() => {
      durationRef.current++;
      setRecordingDuration(durationRef.current);
      setTimeLeft(t => {
        if (t <= 1) { stopRecording(); return 0; }
        return t - 1;
      });
    }, 1000);
  }

  function stopRecording() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    stopMicFaceTracking();
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }

  function uploadAnswerXHR(blob: Blob, qId: number, duration: number, switches: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append("video", blob, blob.type.includes("mp4") ? "answer.mp4" : "answer.webm");
      fd.append("duration_seconds", String(Math.round(duration)));
      fd.append("tab_switches", String(switches));
      fd.append("fullscreen_exits", String(fullscreenExitsRef.current));
      fd.append("rerecord_attempts", String(reRecordAttemptsRef.current[qId] || 0));
      fd.append("no_face_events", String(noFaceEventsRef.current));
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${PUBLIC_API}/api/public/screen/${token}/answer/${qId}`);
      xhr.withCredentials = true;
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 95));
      };
      xhr.timeout = 10 * 60 * 1000;
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) { resolve(); return; }
        // 4xx = validation error (non-retriable). Parse detail so UI can show it directly.
        if (xhr.status >= 400 && xhr.status < 500) {
          let detail = `Server error ${xhr.status}`;
          try { detail = JSON.parse(xhr.responseText)?.detail || detail; } catch { /* ignore */ }
          reject({ retriable: false, message: detail });
        } else {
          reject({ retriable: true, message: `Server error ${xhr.status}` });
        }
      };
      xhr.onerror = () => reject({ retriable: true, message: "Network error" });
      xhr.ontimeout = () => reject({ retriable: false, message: "Upload timed out — check your connection" });
      xhr.send(fd);
    });
  }

  async function autoSubmit(blob: Blob, q: Question, qIndex: number, total: number, switches: number, duration: number) {
    setPhase("uploading");
    setUploadProgress(0);
    const sizeMB = (blob.size / 1024 / 1024).toFixed(1);
    setUploadMsg(`Uploading ${sizeMB} MB…`);

    // Persist the blob before we attempt upload (#2) so a refresh mid-upload can recover it
    // from IndexedDB instead of forcing a re-record. Best-effort — never blocks the upload.
    savePending({ token: token!, questionId: q.id, qIndex, total, duration, switches, blob, savedAt: Date.now() });

    // Retry up to 3 times for network/server errors, but NOT for 4xx validation errors.
    let uploaded = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        if (attempt > 0) {
          setUploadProgress(0);
          setUploadMsg(`Connection issue — retrying (${attempt}/2)…`);
          await new Promise(r => setTimeout(r, 2500 * attempt));
        }
        await uploadAnswerXHR(blob, q.id, duration, switches);
        uploaded = true;
        break;
      } catch (err: any) {
        if (err?.retriable === false) {
          // Validation error (e.g. "too short") — retrying won't help. Discard the blob,
          // go back to "ready" and let the candidate re-record with the error message shown.
          deletePending(token!, q.id);
          // Track how many times this question was re-recorded after rejection.
          reRecordAttemptsRef.current[q.id] = (reRecordAttemptsRef.current[q.id] || 0) + 1;
          setPhase("question");
          setRecordState("ready");
          setRecordError(err.message || "Submission error — please try again.");
          return;
        }
        // Network / 5xx — retriable, keep looping
      }
    }

    if (!uploaded) {
      // 3 network failures — keep blob in IndexedDB, show retry screen.
      setPendingUpload({ blob, q, qIndex, total, switches, duration });
      setPhase("question");
      setRecordState("ready");
      return;
    }

    // Confirmed uploaded — drop the durable copy.
    deletePending(token!, q.id);
    setUploadProgress(100);
    setPendingUpload(null);

    const next = qIndex + 1;
    if (next < total) {
      // After a refresh-recovery the camera stream is gone — we can't record the next
      // question inline. Route through the resume flow, which re-acquires the camera.
      if (!streamRef.current) { await continueAfterRestore(); return; }
      setQuestionIndex(next);
      setRecordState("ready");
      setTabSwitches(0);
      setShowTabWarning(false);
      tabSwitchesRef.current = 0;
      setPhase("question");
      if (liveVideoRef.current && streamRef.current) {
        liveVideoRef.current.srcObject = streamRef.current;
      }
      return;
    }

    // Step 2: complete session — auto-retry up to 3x; video is already saved so don't re-record
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await completePublicScreen(token!);
        clearToken(token!);
        stopStream();
        setPhase("done");
        return;
      } catch (err: any) {
        const status = err?.response?.status || err?.status;
        if (status === 409) { clearToken(token!); stopStream(); setPhase("done"); return; }
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    alert("Your response was saved, but we couldn't finalize the submission. Please reload this page and click 'Submit' to try again.");
    setPhase("question");
    setRecordState("ready");
  }

  // After recovering an upload following a page refresh (no live camera), re-sync with the
  // server and either finalize or hand off to the resume flow for any remaining questions.
  async function continueAfterRestore() {
    try {
      const data = await getPublicScreen(token!);
      setSessionData(data);
      const answeredIds: number[] = data.answered_question_ids || [];
      setAnsweredQuestionIds(answeredIds);
      if (["completed", "processing", "scored", "error"].includes(data.status)) {
        clearToken(token!); setPhase("done"); return;
      }
      const remaining = (data.questions || []).filter((x: Question) => !answeredIds.includes(x.id));
      if (remaining.length === 0) { await handleSubmitAll(); return; }
      setPhase("resume");
    } catch {
      setPhase("resume");
    }
  }

  // Upload-throughput probe (#5): time how long the small tech-check clip takes to reach the
  // server, then warn candidates whose effective upload speed is below the encoder bitrate
  // (~564 kbps) — for them, real answers will be slow to upload. Best-effort and non-blocking.
  async function measureConnection(sampleBlob: Blob) {
    if (!token || sampleBlob.size === 0) return;
    try {
      const fd = new FormData();
      fd.append("sample", sampleBlob, "probe.webm");
      const started = performance.now();
      const resp = await fetch(`${PUBLIC_API}/api/public/screen/${token}/speedtest`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!resp.ok) return;
      const secs = (performance.now() - started) / 1000;
      if (secs <= 0) return;
      const kbps = (sampleBlob.size * 8) / 1000 / secs;
      setConnectionKbps(kbps);
      if (kbps < 600) setConnectionWarn(true);
    } catch {
      /* probe failure should never block the interview */
    }
  }

  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // ── Loading ────────────────────────────────────────────────────────────────
  if (phase === "loading") return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
      <div style={{ color: "#64748b", fontSize: 16 }}>Loading your interview…</div>
    </div>
  );

  if (phase === "expired") return (
    <Wrapper>
      <div style={{ textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>Link Expired</div>
        <div style={{ color: "#64748b" }}>This screening link has expired. Please contact the recruiter if you still want to participate.</div>
      </div>
    </Wrapper>
  );

  if (phase === "abandoned") return (
    <Wrapper>
      <div style={{ textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏱️</div>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>Session Timed Out</div>
        <div style={{ color: "#64748b", lineHeight: 1.7, maxWidth: 400, margin: "0 auto" }}>
          Your interview session expired because it wasn't completed within the 20-minute window.
          Please contact the recruiter to request a new link.
        </div>
      </div>
    </Wrapper>
  );

  if (phase === "already_done") return (
    <Wrapper>
      <div style={{ textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>Interview Submitted</div>
        <div style={{ color: "#64748b" }}>Your interview has been submitted. The recruiter will be in touch soon.</div>
      </div>
    </Wrapper>
  );

  if (phase === "resume") {
    const questions: Question[] = sessionData?.questions || [];
    const answered = answeredQuestionIds;
    const doneCount = answered.length;
    const remaining = questions.filter((q: Question) => !answered.includes(q.id)).length;
    const firstUnansweredIdx = questions.findIndex((q: Question) => !answered.includes(q.id));
    return (
      <Wrapper>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 16px" }}>
          <div style={{ fontSize: 36, textAlign: "center", marginBottom: 16 }}>⚠️</div>
          <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8, textAlign: "center" }}>Resume Your Interview</div>
          <div style={{ color: "#64748b", fontSize: 15, textAlign: "center", marginBottom: 24 }}>
            It looks like your session was interrupted. You can resume from where you left off.
          </div>
          <div style={{ background: "#fefce8", border: "1px solid #fef08a", borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 14, color: "#713f12" }}>
            {remaining > 0 ? (
              <><strong>Important:</strong> Already-answered questions ({doneCount} of {questions.length}) cannot be re-recorded.
              You will continue from Question {firstUnansweredIdx + 1}. Your previous answers are saved.</>
            ) : (
              <><strong>All {questions.length} responses are saved.</strong> Click below to finalize your submission.</>
            )}
          </div>
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, marginBottom: 24, fontSize: 14 }}>
            {questions.map((q: Question, i: number) => {
              const isDone = answered.includes(q.id);
              return (
                <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < questions.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <span style={{ fontSize: 16 }}>{isDone ? "✅" : "⬜"}</span>
                  <span style={{ color: isDone ? "#64748b" : "#94a3b8", fontSize: 13, fontStyle: isDone ? "normal" : "italic" }}>
                    {isDone
                      ? `Q${i + 1}: ${q.question_text.slice(0, 60)}${q.question_text.length > 60 ? "…" : ""}`
                      : `Q${i + 1}: Question hidden until recording starts`}
                  </span>
                  {isDone && <span style={{ marginLeft: "auto", fontSize: 11, color: "#16a34a", fontWeight: 700 }}>Saved</span>}
                </div>
              );
            })}
          </div>
          {remaining > 0 ? (
            <AsyncButton onClick={handleResume} style={{ ...btnStyle, width: "100%", fontSize: 16 }}>
              Resume — Answer {remaining} Remaining Question{remaining !== 1 ? "s" : ""}
            </AsyncButton>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 14, color: "#64748b" }}>All responses are saved. Click below to finalize your submission.</div>
              <button onClick={handleSubmitAll} style={{ ...btnStyle, width: "100%", fontSize: 16 }}>
                Submit Interview
              </button>
            </div>
          )}
        </div>
      </Wrapper>
    );
  }

  if (phase === "error") return (
    <Wrapper>
      <div style={{ textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>Something went wrong</div>
        <div style={{ color: "#64748b" }}>{errorMsg || "Please try again or contact the recruiter."}</div>
      </div>
    </Wrapper>
  );

  if (phase === "done") return (
    <Wrapper>
      <div style={{ textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
        <div style={{ fontWeight: 700, fontSize: 24, marginBottom: 12 }}>Interview Submitted!</div>
        <div style={{ color: "#475569", fontSize: 16, lineHeight: 1.7, maxWidth: 420, margin: "0 auto" }}>
          Thank you for completing the video interview. Your responses are being reviewed by AI and the recruiter will be in touch soon.
        </div>
      </div>
    </Wrapper>
  );

  if (phase === "uploading") return (
    <Wrapper>
      <div style={{ textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>⬆️</div>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Uploading your response…</div>
        <div style={{ color: "#64748b", fontSize: 13, marginBottom: 20 }}>{uploadMsg || "Please keep this page open."}</div>
        <div style={{ background: "#e2e8f0", borderRadius: 999, height: 10, maxWidth: 300, margin: "0 auto" }}>
          <div style={{ height: "100%", borderRadius: 999, background: "#0f766e", width: `${uploadProgress}%`, transition: "width 0.4s" }} />
        </div>
        <div style={{ color: "#64748b", marginTop: 10, fontSize: 14 }}>{uploadProgress}%</div>
        <div style={{ color: "#94a3b8", marginTop: 16, fontSize: 12 }}>Large video files may take a minute on slow connections — do not close this tab.</div>
      </div>
    </Wrapper>
  );

  // ── Consent ────────────────────────────────────────────────────────────────
  if (phase === "consent") {
    const sd = sessionData;
    return (
      <Wrapper>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 16px" }}>
          <div style={{ fontWeight: 700, fontSize: 24, marginBottom: 4 }}>Video Interview</div>
          <div style={{ color: "#64748b", marginBottom: 20, fontSize: 15 }}>
            {sd?.job_title && <><strong>{sd.job_title}</strong> at </>}<strong>{sd?.company_name}</strong>
          </div>

          {sd?.intro_text && (
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 14, color: "#166534", lineHeight: 1.6 }}>
              {sd.intro_text}
            </div>
          )}

          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, marginBottom: 24 }}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>What to expect</div>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2, fontSize: 14, color: "#475569" }}>
              <li>Camera &amp; mic check before you start</li>
              <li>One unscored warm-up question to get comfortable</li>
              <li>{totalQuestions} question{totalQuestions !== 1 ? "s" : ""} to answer on video</li>
              <li>Each question has a time limit ({sd?.time_limit_seconds ? fmtTime(sd.time_limit_seconds) : "2:00"} default)</li>
              <li>No retakes — record your best answer in one take</li>
              <li>Stay on this tab and in fullscreen while recording — switching tabs, minimizing, or exiting fullscreen is logged and shown to the recruiter</li>
              <li>Each answer must be at least 15 seconds long, or you'll be asked to re-record it</li>
            </ul>
          </div>

          <div style={{ background: "#fefce8", border: "1px solid #fef08a", borderRadius: 8, padding: 14, marginBottom: 24, fontSize: 13, color: "#713f12" }}>
            <strong>Consent notice:</strong> By clicking "Allow Camera &amp; Start", you consent to your video responses being recorded, stored, and reviewed by the hiring team and AI for screening purposes. Tab switches, exiting fullscreen, and re-recording attempts are tracked during your session and included in the recruiter's review.
          </div>

          <AsyncButton onClick={handleStart} style={{ ...btnStyle, width: "100%", fontSize: 16 }}>
            Allow Camera &amp; Start Interview
          </AsyncButton>
        </div>
      </Wrapper>
    );
  }

  // ── Tech Check ────────────────────────────────────────────────────────────
  if (phase === "techcheck") {
    return (
      <Wrapper>
        <BrandHeader companyName={sessionData?.company_name} jobTitle={sessionData?.job_title} logoUrl={logoUrl} />
        <div style={{ padding: "28px 24px" }}>
          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 4 }}>Camera &amp; Mic Check</div>
          <div style={{ color: "#64748b", fontSize: 14, marginBottom: 20 }}>Make sure your camera and microphone are working before you start.</div>

          {connectionWarn && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
              <strong>⚠️ Slow connection detected{connectionKbps ? ` (~${Math.round(connectionKbps)} kbps)` : ""}.</strong> Your
              answers will still upload, but it may take longer after each question. If possible, switch
              to Wi-Fi or move somewhere with a stronger signal before you begin. Don't close the tab while uploading.
            </div>
          )}

          <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#000", aspectRatio: "16/9", marginBottom: 16 }}>
            {techState !== "playback" && (
              <video ref={liveVideoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
            {techState === "playback" && (
              <video ref={techPlaybackRef} controls playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
            {techState === "recording" && (
              <div style={{ position: "absolute", top: 10, left: 10, display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.6)", borderRadius: 20, padding: "4px 10px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>Recording…</span>
              </div>
            )}
          </div>

          {techState === "preview" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontSize: 14, color: "#475569", margin: 0 }}>You should see yourself above. When ready, record a 5-second clip to test your audio and video.</p>
              <button onClick={startTechRecording} style={btnStyle}>Record 5-second Test</button>
            </div>
          )}

          {techState === "recording" && (
            <p style={{ textAlign: "center", color: "#64748b", fontSize: 14 }}>Recording for 5 seconds — say something to test your microphone…</p>
          )}

          {techState === "playback" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontSize: 14, color: "#475569", margin: 0 }}>Watch the playback. Can you see and hear yourself clearly?</p>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={playTechRecording} style={outlineBtn}>▶ Play Again</button>
                <button onClick={retryTechCheck} style={outlineBtn}>Retry</button>
                <button onClick={confirmTechCheck} style={btnStyle}>Looks Good — Continue</button>
              </div>
            </div>
          )}
        </div>
      </Wrapper>
    );
  }

  // ── Practice Question ─────────────────────────────────────────────────────
  if (phase === "practice") {
    const limit = 60;
    const timePct = practiceTimeLeft > 0 ? (practiceTimeLeft / limit) * 100 : 0;
    const timeColor = practiceTimeLeft < 15 ? "#dc2626" : "#0f766e";
    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", color: "#fff", display: "flex", flexDirection: "column" }}>
        <BrandHeader companyName={sessionData?.company_name} jobTitle={sessionData?.job_title} logoUrl={logoUrl} isPractice />
        {slowConnection && (
          <div style={{ background: "#78350f", borderBottom: "1px solid #92400e", padding: "8px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#fef3c7" }}>⚠️ Slow connection detected — uploads may take longer. Stay on this page after each recording.</span>
            <button onClick={() => setSlowConnection(false)} style={{ background: "none", border: "none", color: "#fde68a", cursor: "pointer", fontSize: 16, padding: 4 }}>✕</button>
          </div>
        )}
        <div style={{ padding: "10px 20px", background: "#1e293b", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Warm-up Practice</div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>Not scored — just to get comfortable</div>
          </div>
          <button onClick={skipPractice} style={{ background: "none", border: "1px solid #475569", borderRadius: 6, padding: "6px 14px", color: "#94a3b8", cursor: "pointer", fontSize: 13 }}>
            Skip →
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 16px" }}>
          {practiceState !== "ready" && (
            <div style={{ maxWidth: 640, width: "100%", marginBottom: 20 }}>
              <div style={{ background: "#1e293b", borderRadius: 10, padding: 20, fontSize: 17, lineHeight: 1.6, fontWeight: 500 }}>
                {practiceQ}
              </div>
            </div>
          )}

          <div style={{ position: "relative", width: "100%", maxWidth: 520, borderRadius: 12, overflow: "hidden", background: "#000", aspectRatio: "16/9", marginBottom: 20 }}>
            <video ref={liveVideoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />

            {practiceState === "ready" && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 24 }}>
                <div style={{ fontSize: 18, fontWeight: 700, textAlign: "center" }}>Practice Question</div>
                <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "center" }}>Not scored — just to help you get comfortable recording.</div>
                <button onClick={startPracticeCountdown} style={{ background: "#0f766e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 32px", cursor: "pointer", fontWeight: 700, fontSize: 15 }}>
                  ⏺ Start Practice
                </button>
              </div>
            )}

            {practiceState === "countdown" && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 96, fontWeight: 900, color: "#fff" }}>{practiceCountdown}</div>
              </div>
            )}

            {practiceState === "recording" && (
              <>
                <div style={{ position: "absolute", top: 12, left: 12, display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.6)", borderRadius: 20, padding: "4px 10px" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", animation: "pulse 1s infinite" }} />
                  <span style={{ fontSize: 12, fontWeight: 700 }}>PRACTICE {fmtTime(practiceRecordingDur)}</span>
                </div>
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "rgba(255,255,255,0.2)" }}>
                  <div style={{ height: "100%", background: timeColor, width: `${timePct}%`, transition: "width 1s linear" }} />
                </div>
              </>
            )}
          </div>

          {practiceState === "recording" && (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span style={{ color: timeColor, fontWeight: 700, fontSize: 18 }}>{fmtTime(practiceTimeLeft)}</span>
              <button onClick={stopPracticeRecording} style={{ ...outlineBtn, borderColor: "#ef4444", color: "#ef4444" }}>
                ⏹ Done
              </button>
            </div>
          )}

          {practiceState === "done" && (
            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
              <div style={{ fontSize: 16, color: "#94a3b8" }}>Practice done! Ready for the real questions?</div>
              <div style={{ display: "flex", gap: 12 }}>
                <button onClick={() => { setPracticeState("ready"); setPracticeTimeLeft(60); }} style={outlineBtn}>Redo Practice</button>
                <button onClick={goToFirstQuestion} style={btnStyle}>Start Real Interview →</button>
              </div>
            </div>
          )}
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>
      </div>
    );
  }

  // ── Question / Recording ───────────────────────────────────────────────────
  if (phase === "question" && pendingUpload) {
    const sizeMB = (pendingUpload.blob.size / 1024 / 1024).toFixed(1);
    return (
      <Wrapper>
        <div style={{ textAlign: "center", padding: 40, maxWidth: 480, margin: "0 auto" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📡</div>
          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8, color: "#dc2626" }}>Upload Failed</div>
          <div style={{ color: "#64748b", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            We couldn't upload your {sizeMB} MB response after 3 attempts. Your recording is still saved — you do <strong>not</strong> need to re-record. Check your connection and try again.
          </div>
          <button
            onClick={() => autoSubmit(pendingUpload.blob, pendingUpload.q, pendingUpload.qIndex, pendingUpload.total, pendingUpload.switches, pendingUpload.duration)}
            style={{ ...btnStyle, marginBottom: 12, width: "100%" }}
          >
            Retry Upload
          </button>
          <div style={{ color: "#94a3b8", fontSize: 12 }}>If this keeps failing, check your internet speed or try on a stronger connection.</div>
        </div>
      </Wrapper>
    );
  }

  if (phase === "question" && currentQuestion) {
    const limit = currentQuestion.time_limit_seconds || sessionData?.time_limit_seconds || 120;
    const timePct = timeLeft > 0 ? (timeLeft / limit) * 100 : 0;
    const timeColor = timeLeft < 30 ? "#dc2626" : timeLeft < 60 ? "#d97706" : "#0f766e";
    const questionVisible = recordState !== "ready";

    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", color: "#fff", display: "flex", flexDirection: "column" }}>
        <BrandHeader companyName={sessionData?.company_name} jobTitle={sessionData?.job_title} logoUrl={logoUrl} />

        <div style={{ padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>Question {questionIndex + 1} of {totalQuestions}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {sessionSecondsLeft !== null && sessionSecondsLeft < 5 * 60 && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, background: sessionSecondsLeft < 2 * 60 ? "#7f1d1d" : "#78350f", borderRadius: 20, padding: "3px 10px" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: sessionSecondsLeft < 2 * 60 ? "#fca5a5" : "#fef3c7" }}>
                  ⏱ {fmtTime(sessionSecondsLeft)} left
                </span>
              </div>
            )}
            {recordState === "recording" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ef4444", animation: "pulse 1s infinite" }} />
                <span style={{ color: timeColor, fontWeight: 700, fontSize: 18 }}>{fmtTime(timeLeft)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Slow connection warning */}
        {slowConnection && (
          <div style={{ background: "#78350f", borderBottom: "1px solid #92400e", padding: "8px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#fef3c7" }}>
              ⚠️ Slow connection detected — your uploads may take longer. Stay on this page until each response finishes uploading.
            </span>
            <button onClick={() => setSlowConnection(false)} style={{ background: "none", border: "none", color: "#fde68a", cursor: "pointer", fontSize: 16, padding: 4 }}>✕</button>
          </div>
        )}

        {/* Tab-switch warning */}
        {showTabWarning && (
          <div style={{ background: "#7f1d1d", borderBottom: "1px solid #991b1b", padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              ⚠️ Tab switch detected ({tabSwitches}x) — this is logged and visible to the recruiter.
            </span>
            <button onClick={() => setShowTabWarning(false)} style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>
        )}

        {/* Record/submit error (e.g. "too short") — dismissible, reset on next attempt */}
        {recordError && (
          <div style={{ background: "#7f1d1d", borderBottom: "1px solid #991b1b", padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#fca5a5" }}>⚠ {recordError}</span>
            <button onClick={() => setRecordError("")} style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>
        )}

        {/* Face not visible warning */}
        {faceWarning && (
          <div style={{ background: "#713f12", borderBottom: "1px solid #92400e", padding: "8px 24px", fontSize: 13, fontWeight: 600, color: "#fef3c7" }}>
            ⚠️ Face not detected — ensure you are visible in the camera frame.
          </div>
        )}

        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 16px 24px" }}>

          {questionVisible && (
            <div style={{ maxWidth: 640, width: "100%", marginBottom: 20 }}>
              <div style={{ background: "#1e293b", borderRadius: 10, padding: 20, fontSize: 17, lineHeight: 1.6, fontWeight: 500 }}>
                {currentQuestion.question_text}
              </div>
            </div>
          )}

          <div style={{ position: "relative", width: "100%", maxWidth: 520, borderRadius: 12, overflow: "hidden", background: "#000", aspectRatio: "16/9", marginBottom: 20 }}>
            <video ref={liveVideoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />

            {recordState === "ready" && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 24 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", textAlign: "center" }}>
                  Ready for Question {questionIndex + 1}?
                </div>
                <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "center" }}>
                  The question will appear when you start recording.
                </div>
                <button onClick={startCountdown} style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, padding: "12px 32px", cursor: "pointer", fontWeight: 700, fontSize: 16, marginTop: 8 }}>
                  ⏺ Start Recording
                </button>
              </div>
            )}

            {recordState === "countdown" && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 96, fontWeight: 900, color: "#fff", lineHeight: 1 }}>{countdown}</div>
              </div>
            )}

            {recordState === "recording" && (
              <div style={{ position: "absolute", top: 12, left: 12, display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.6)", borderRadius: 20, padding: "4px 10px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
                <span style={{ fontSize: 12, fontWeight: 700 }}>REC {fmtTime(recordingDuration)}</span>
              </div>
            )}

            {/* Mic level indicator */}
            {recordState === "recording" && (
              <div style={{ position: "absolute", bottom: 16, right: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                <div style={{ width: 8, height: 52, background: "rgba(0,0,0,0.4)", borderRadius: 4, display: "flex", flexDirection: "column-reverse", overflow: "hidden" }}>
                  <div style={{ width: "100%", height: `${micLevel}%`, background: micLevel > 60 ? "#22c55e" : micLevel > 15 ? "#0f766e" : "#ef4444", transition: "height 0.08s" }} />
                </div>
                <span style={{ fontSize: 9, color: "rgba(255,255,255,0.6)" }}>🎤</span>
              </div>
            )}

            {recordState === "recording" && (
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "rgba(255,255,255,0.2)" }}>
                <div style={{ height: "100%", background: timeColor, width: `${timePct}%`, transition: "width 1s linear" }} />
              </div>
            )}
          </div>

          {recordState === "recording" && (
            <button onClick={stopRecording} style={{ ...outlineBtn, borderColor: "#ef4444", color: "#ef4444", fontSize: 16 }}>
              ⏹ Done
            </button>
          )}
        </div>

        <PoweredByFooter current={questionIndex + 1} total={totalQuestions} />
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>
      </div>
    );
  }

  return null;
}

function Wrapper({ children }: React.PropsWithChildren<object>) {
  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.08)", maxWidth: 600, width: "100%" }}>
        {children}
      </div>
    </div>
  );
}
